import cloneDeep from 'lodash/cloneDeep';
import { Signal } from 'typed-signals';
import { getRandomElement, removeElement } from 'utils/array-utils';
import { isHpBlocker } from 'editor/HTML/panels/EditorBlockersPanel';
import type { LevelTaskProgress } from 'game/tasks/types';
import { LevelTaskType, levelTaskToBlocker } from 'game/tasks/types';
import { v4 } from 'uuid';
import type { BlockerMatchParams, BlockerModel, BlockerType, RocketBlockerState } from '../../types';
import type { TileStackModel } from '../board/TileStackModel';
import type { BoardModel } from '../board/BoardModel';
import { TileRemovalReason } from '../board/BoardEvents';
import { PlankBlockerModel } from './PlankBlockerModel';
import { TapeBlockerModel } from './TapeBlockerModel';
import { KnotBlockerModel } from './KnotBlockerModel';
import { BirdHouseBlockerModel } from './BirdHouseBlockerModel';
import { MeasureTapeBlockerModel } from './MeasureTapeBlockerModel';
import type { TasksRunner } from '../../../../libs/TasksRunner';

let instanceIndex = 0;

export const ROCKET_FLY_DURATION = 900;
export const ROCKET_BLOCKER_LAUNCH_DELAY = 100;

export class RocketBlockerModel implements BlockerModel {
  // eslint-disable-next-line no-plusplus
  public readonly id = `rocket_${instanceIndex++}`;

  public readonly requestRemoval = new Signal();

  public readonly onModelChanged = new Signal();

  public readonly maxHp = 4;

  public readonly destructible = true;

  private readonly state: RocketBlockerState;

  public readonly underTile = false;

  private readonly levelTaskProgress = new Map<LevelTaskType, LevelTaskProgress>();

  public targets: TileStackModel[] = [];

  /**
   * Index of the rocket launch. Used to delay the rocket launch.
   */
  private static rocketLaunchIndex = 0;

  constructor(
    private readonly stack: TileStackModel,
    private readonly boardModel: BoardModel,
    private readonly taskRunner: TasksRunner,
    state: RocketBlockerState,
  ) {
    this.state = cloneDeep(state);
  }

  public onMatch({ stack, distantMatch }: BlockerMatchParams): void {
    if (!this.stack.isNeighborOf(stack) && !distantMatch) return;

    RocketBlockerModel.rocketLaunchIndex += 1;
    this.taskRunner.addTask(this.fireRocket());
  }

  private *fireRocket(): Generator<void, void> {
    yield* this.boardModel
      .getScheduler()
      .waitGenerator(null, ROCKET_BLOCKER_LAUNCH_DELAY * RocketBlockerModel.rocketLaunchIndex);

    if (this.state.hp === 0) return;

    RocketBlockerModel.rocketLaunchIndex -= 1;
    this.state.hp -= 1;

    const target = this.findRocketTarget();
    if (target) this.targets.push(target);

    this.onModelChanged.emit();

    if (this.state.hp <= 0) {
      this.requestRemoval.emit();
    }

    yield* this.boardModel.getScheduler().waitGenerator(null, ROCKET_FLY_DURATION);

    if (target) {
      this.hitRocketTarget(target);
      removeElement(this.targets, target);
    }
  }

  public onLevelBlockerTaskUpdate(taskType: LevelTaskType, progress: LevelTaskProgress): void {
    this.levelTaskProgress.set(taskType, progress);
  }

  public getHp(): number {
    return this.state.hp;
  }

  public setHp(hp: number): void {
    this.state.hp = hp;
    this.onModelChanged.emit();
  }

  public exportState(): RocketBlockerState {
    return this.state;
  }

  public getType(): BlockerType {
    return this.state.type;
  }

  /**
   * Determines the rocket's target based on the following priority:
   * 1. blockers that are part of the level objectives (PlankBlocker, TapeBlocker, KnotBlocker, BirdHouseBlocker)*
   * 2. if tiles are a level objective, targets stacks containing tiles
   * 3. any remaining targetable blockers(*) not tied to objectives
   * 4. any stack containing tiles
   * 5. pick a random empty stack
   *
   * additional conditions:
   * - the target is not already targeted by too many rockets
   * - targeting a blocker won't exceed the level objective requirements
   *
   * @returns {TileStackModel | null} The selected target stack or null if no valid target found
   */
  private findRocketTarget(): TileStackModel | null {
    const stacks = this.boardModel.getStacks();
    const targetedStacks = this.getTargetedStacks(stacks);

    const targetableBlockerStacks = this.getTargetableBlockerStacks(stacks, targetedStacks);

    // prio 1: target blockers that are part of level objectives
    const prioritizedBlockerStacks = targetableBlockerStacks.filter((stack) =>
      [...this.levelTaskProgress.keys()].some(
        (taskType) => levelTaskToBlocker.get(taskType) === stack.blocker?.getType(),
      ),
    );

    if (prioritizedBlockerStacks.length > 0) {
      return getRandomElement(prioritizedBlockerStacks);
    }

    const tileStacks = this.getTileStacks(stacks, targetedStacks);

    // prio 2: if tiles are an objective, target tile stacks
    if (this.levelTaskProgress.has(LevelTaskType.Hexes)) {
      if (tileStacks.length > 0) {
        return getRandomElement(tileStacks);
      }
    }

    // prio 3: target any remaining non-objective blockers
    if (targetableBlockerStacks.length > 0) {
      return getRandomElement(targetableBlockerStacks);
    }

    // prio 4: target any remaining tile stacks
    if (tileStacks.length > 0) {
      return getRandomElement(tileStacks);
    }

    // prio 5: target any empty stack as last resort
    const emptyStacks = this.getEmptyStacks(stacks, targetedStacks);
    if (emptyStacks.length > 0) {
      return getRandomElement(emptyStacks);
    }

    return null;
  }

  /**
   * Retrieves stacks targeted by RocketBlockerModel instances.
   *
   * @param stacks - Array of TileStackModel objects.
   * @returns Array of targeted TileStackModel objects.
   */
  private getTargetedStacks(stacks: TileStackModel[]): TileStackModel[] {
    return stacks
      .flatMap((stack) => {
        if (stack.blocker instanceof RocketBlockerModel && stack.blocker.targets) {
          return stack.blocker.targets;
        }
        return null;
      })
      .filter(Boolean) as TileStackModel[];
  }

  /**
   * Returns targetable blocker stacks by verifying if they can be targeted.
   *
   * @param stacks - Array of TileStackModel to filter.
   * @param targetedStacks - Array of already targeted TileStackModel.
   * @returns Array of targetable TileStackModel.
   */
  private getTargetableBlockerStacks(stacks: TileStackModel[], targetedStacks: TileStackModel[]): TileStackModel[] {
    const targetableBlockerStacks = stacks.filter(
      (stack) =>
        stack.blocker &&
        (stack.blocker instanceof PlankBlockerModel ||
          stack.blocker instanceof TapeBlockerModel ||
          stack.blocker instanceof KnotBlockerModel ||
          stack.blocker instanceof MeasureTapeBlockerModel ||
          stack.blocker instanceof BirdHouseBlockerModel),
    );

    // based on the stacks that have a blocker that can be targeted...
    return targetableBlockerStacks.filter((possibleTarget: TileStackModel) => {
      if (!possibleTarget.blocker) return false;

      const targetedByAmount = targetedStacks.filter((stack) => stack === possibleTarget).length;

      if (targetedStacks.includes(possibleTarget)) {
        // ... filter out stacks, if hitting them would decrease its blocker hp to lower than 0
        if (isHpBlocker(possibleTarget.blocker)) {
          const hp = possibleTarget.blocker.getHp();
          if (hp - targetedByAmount === 0) {
            return false;
          }
        }
        // ... filter out other non hp blockers, that are already targeted
        else {
          return false;
        }
      }

      // ... filter out stacks, if hitting them would overshoot the level task goal
      if (possibleTarget.blocker instanceof BirdHouseBlockerModel) {
        const birdTaskProgress = this.levelTaskProgress.get(LevelTaskType.Birds);
        if (!birdTaskProgress) {
          return false;
        }

        const hitsLeft = birdTaskProgress.goal - birdTaskProgress.collected - targetedByAmount;
        if (hitsLeft <= 0) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Returns stacks with tiles that can be targeted. Filters out stacks that are blockers or already targeted.
   */
  private getTileStacks(stacks: TileStackModel[], targetedStacks: TileStackModel[]): TileStackModel[] {
    return stacks
      .filter((stack) => !stack.blocker && stack.hasTiles())
      .filter((stack) => !targetedStacks.includes(stack));
  }

  /**
   * Returns empty stacks that can be targeted. Filters out stacks that are blockers or already targeted.
   */
  private getEmptyStacks(stacks: TileStackModel[], targetedStacks: TileStackModel[]): TileStackModel[] {
    return stacks
      .filter((stack) => !stack.blocker && !stack.isBlocked() && !stack.hasTiles())
      .filter((stack) => !targetedStacks.includes(stack));
  }

  private hitRocketTarget(target: TileStackModel): void {
    if (target.blocker) {
      target.blocker?.onMatch?.({
        matchId: `distant_rocket_match_${v4()}`,
        stack: target,
        distantMatch: true,
        tilesRemoved: [],
      } as BlockerMatchParams);
    } else if (target.hasTiles()) {
      this.boardModel.removeTilesFromStack({
        stack: target,
        reason: TileRemovalReason.Blocker,
        tilesAmount: target.getTileCount(),
      });
    }
  }
}
